import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CircularProgress,
  Typography,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { KomootPreviewMetadata, loadKomootMetadata } from '../utils/komootPreview';

interface KomootPreviewCardProps {
  url: string;
  compact?: boolean;
  stopPropagation?: boolean;
  sx?: SxProps<Theme>;
}

const KomootPreviewCard: React.FC<KomootPreviewCardProps> = ({
  url,
  compact = false,
  stopPropagation = false,
  sx,
}) => {
  const [metadata, setMetadata] = useState<KomootPreviewMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    let active = true;

    setLoading(true);

    loadKomootMetadata(url).then((nextMetadata) => {
      if (!active) {
        return;
      }

      setMetadata(nextMetadata);
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [url]);

  if (!loading && !metadata) {
    return null;
  }

  const handleClick = stopPropagation
    ? (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.stopPropagation();
    }
    : undefined;

  const previewUrl = metadata?.url || url;
  const formattedDistance = metadata?.distanceKm !== null && metadata?.distanceKm !== undefined
    ? new Intl.NumberFormat(i18n.resolvedLanguage, {
      minimumFractionDigits: Number.isInteger(metadata.distanceKm) ? 0 : 1,
      maximumFractionDigits: Number.isInteger(metadata.distanceKm) ? 0 : 1,
    }).format(metadata.distanceKm)
    : null;

  return (
    <Card
      variant="outlined"
      sx={{
        overflow: 'hidden',
        borderColor: 'divider',
        ...sx,
      }}
    >
      <CardActionArea
        component="a"
        href={previewUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
          minHeight: compact ? 96 : 132,
        }}
      >
        {metadata?.imageUrl && (
          <Box
            component="img"
            src={metadata.imageUrl}
            alt={metadata.title || metadata.siteName || 'Komoot'}
            sx={{
              width: compact ? 112 : 180,
              minWidth: compact ? 112 : 180,
              alignSelf: 'stretch',
              objectFit: 'cover',
              bgcolor: 'action.hover',
            }}
          />
        )}

        <Box sx={{ p: compact ? 1.25 : 1.5, minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography variant="overline" color="primary.main" sx={{ lineHeight: 1.2 }}>
            {metadata?.siteName || 'Komoot'}
          </Typography>

          {loading ? (
            <Box sx={{ py: 1 }}>
              <CircularProgress size={18} />
            </Box>
          ) : (
            <>
              <Typography
                variant={compact ? 'subtitle2' : 'subtitle1'}
                sx={{
                  mt: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: compact ? 2 : 3,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {metadata?.title || metadata?.siteName || 'Komoot'}
              </Typography>

              {formattedDistance && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {t('common.labels.distanceKilometers', { value: formattedDistance })}
                </Typography>
              )}

              {metadata?.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: compact ? 2 : 3,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {metadata.description}
                </Typography>
              )}

              <Typography variant="caption" color="primary.main" sx={{ mt: 1, display: 'block' }}>
                {t('activities.viewOnKomoot')}
              </Typography>
            </>
          )}
        </Box>
      </CardActionArea>
    </Card>
  );
};

export default KomootPreviewCard;